import { rgba } from 'polished';
import { PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import * as graphql from '@treams/treams-network';

import { ArrowDownIcon, ArrowUpIcon, ChevronLeftIcon, ChevronRightIcon } from '../icons';
import { tokens } from '../theme/tokens';
import { GraphQLErrorMessage } from './GraphQLErrorMessage';
import { Skeleton } from './Skeleton';
import { Spinner } from './Spinner';
import { Paragraph } from './Text';
import { Button } from './button/Button';

const TableContainer = styled(Box)<{ $embedded?: boolean }>`
	background: ${tokens.surface.white};
	overflow-x: auto;
	overflow-y: hidden;

	${(props) =>
		!props.$embedded &&
		`
		border: 1px solid ${tokens.border.subtle};
		box-shadow: ${tokens.shadow.xs};
		border-radius: ${tokens.radius.md}px;
	`}

	${(props) =>
		props.$embedded &&
		`
		> *:first-child {
			border-top: 1px solid ${tokens.border.subtle};
			border-top-left-radius: 0;
			border-top-right-radius: 0;
		}
	`}

	> *:last-child [data-table-row] {
		border-bottom: none;
	}
`;

export function Table(props: PropsWithChildren<{ embedded?: boolean }>) {
	return <TableContainer $embedded={props.embedded}>{props.children}</TableContainer>;
}

const TableCellContainer = styled(Flex)<{
	interactive?: boolean;
	$grow?: boolean;
	$fixed?: boolean;
	$width?: number;
	$minWidth?: number;
}>`
	padding: 0;
	line-height: ${tokens.typography.lineHeights.body};
	/* A cell is sized by its column, never by its content, so cell content must
	 * not widen the row it sits in. */
	contain: inline-size;
	min-width: ${(props) => (props.$minWidth === undefined ? '0' : `${props.$minWidth}px`)};
	flex-grow: ${(props) => (props.$grow ? 1 : 0)};
	flex-shrink: ${(props) => (props.$grow ? 1 : props.$fixed ? 0 : 1)};
	width: ${(props) => (props.$width ? `${props.$width}px` : undefined)};
	gap: ${tokens.gap.element}px;
	align-items: center;

	${(props) =>
		props.interactive &&
		`
		cursor: pointer;

		:hover {
			color: ${tokens.text.secondary};
		}

		:hover [data-sort-indicator] {
			color: ${tokens.icon.secondary};
		}
	`}
`;

const TableCellSortIndicator = styled.span`
	display: flex;
	flex-shrink: 0;
	color: ${tokens.icon.tertiary};

	svg {
		width: 14px;
		height: 14px;
		display: block;
		color: inherit;
	}
`;

const CellSkeleton = styled(Skeleton.Box)`
	&& {
		background: linear-gradient(
			-45deg,
			${tokens.surface.skeleton} 0%,
			${tokens.border.subtle} 10%,
			${tokens.border.subtle} 40%,
			${tokens.surface.skeleton} 50%,
			${tokens.border.subtle} 60%,
			${tokens.border.subtle} 90%,
			${tokens.surface.skeleton} 100%
		);
		background-attachment: fixed;
		background-size: 400px 400px;
		background-repeat: repeat;
	}
`;

function TableCell(
	props: PropsWithChildren<{
		align?: 'left' | 'right' | 'center';
		basis?: number;
		width?: number;
		minWidth?: number;
		grow?: boolean;
		sorted?: 'asc' | 'desc';
		onClick?: () => void;
		tightLayout?: boolean;
	}>,
) {
	const justifyContent =
		props.align === 'right' ? 'flex-end' : props.align === 'center' ? 'center' : 'flex-start';
	const fixed = props.width !== undefined;
	const grow = Boolean(props.grow);
	const basis = props.basis ?? 1;

	return (
		<TableCellContainer
			flexBasis={fixed || grow ? undefined : (basis * 100).toFixed(0) + '%'}
			justifyContent={justifyContent}
			interactive={props.onClick !== undefined}
			onClick={props.onClick}
			$grow={grow}
			$fixed={fixed}
			$width={props.width}
			$minWidth={props.minWidth}
		>
			{props.children}
			{props.sorted && (
				<TableCellSortIndicator data-sort-indicator>
					{props.sorted === 'asc' ? <ArrowUpIcon /> : <ArrowDownIcon />}
				</TableCellSortIndicator>
			)}
		</TableCellContainer>
	);
}

Table.Cell = TableCell;

function TableCellSkeleton(
	props: PropsWithChildren<{ basis?: number; width?: number; minWidth?: number; grow?: boolean }>,
) {
	const fixed = props.width !== undefined;
	const grow = Boolean(props.grow);
	const basis = props.basis ?? 1;

	return (
		<TableCellContainer
			flexBasis={fixed || grow ? undefined : (basis * 100).toFixed(0) + '%'}
			$grow={grow}
			$fixed={fixed}
			$width={props.width}
			$minWidth={props.minWidth}
		>
			<CellSkeleton height={20} flexGrow={1} />
		</TableCellContainer>
	);
}

TableCell.Skeleton = TableCellSkeleton;

const TableRowContainer = styled(Flex)<{ tall?: boolean; selected?: boolean }>`
	align-items: center;
	/* Grow past the container rather than letting columns that cannot shrink
	 * overlap each other; the container scrolls horizontally instead. */
	min-width: min-content;
	gap: ${tokens.gap.group}px;
	min-height: ${(props) => (props.tall ? 56 : 40)}px;
	padding: ${tokens.padding.control}px ${tokens.padding.container}px;
	box-sizing: border-box;
	border-bottom: 1px solid ${tokens.border.subtle};
	color: ${tokens.text.primary};
	background: ${(props) =>
		props.selected ? rgba(props.theme.colors.primary, 0.08) : tokens.surface.white};

	&:last-child {
		border-bottom: none;
	}

	${(props) =>
		props.onClick &&
		`
		cursor: pointer;
		:hover {
			background-color: ${props.selected ? rgba(props.theme.colors.primary, 0.08) : tokens.surface.hover};
		}
	`}
`;

const TableRowLink = styled(Link)`
	display: block;

	${TableRowContainer}:last-child {
		border-bottom: 1px solid ${tokens.border.subtle};
	}

	:last-child {
		${TableRowContainer} {
			border-bottom: 0;
			border-radius: 0 0 ${tokens.radius.md}px ${tokens.radius.md}px;
		}
	}

	:hover ${TableRowContainer}:not([data-selected]) {
		color: ${tokens.text.primary};
		background-color: ${tokens.surface.hover};
	}
`;

function TableRow(
	props: PropsWithChildren<{
		tall?: boolean;
		link?: string;
		selected?: boolean;
		onClick?: () => void;
	}>,
) {
	const row = (
		<TableRowContainer
			data-table-row
			data-selected={props.selected || undefined}
			onClick={props.onClick}
			tall={props.tall}
			selected={props.selected}
		>
			{props.children}
		</TableRowContainer>
	);

	if (props.link) {
		return <TableRowLink to={props.link}>{row}</TableRowLink>;
	}
	return row;
}

Table.Row = TableRow;

const TableHeaderContainer = styled(Flex)`
	align-items: center;
	min-width: min-content;
	gap: ${tokens.gap.group}px;
	padding: ${tokens.padding.control}px ${tokens.padding.container}px;
	background: ${tokens.surface.primary};
	border-top-left-radius: ${tokens.radius.md}px;
	border-top-right-radius: ${tokens.radius.md}px;
	border-bottom: 1px solid ${tokens.border.subtle};

	&:last-child {
		border-bottom: none;
		border-radius: ${tokens.radius.md}px;
	}

	${TableCellContainer} {
		font-size: ${tokens.typography.sizes.small}px;
		font-weight: ${tokens.typography.weights.regular};
		letter-spacing: 0.01em;
		line-height: 14px;
		color: ${tokens.text.secondary};
		align-items: center;

		${TableCellSortIndicator} {
			margin-inline-start: -${tokens.gap.element - tokens.gap.inline / 2}px;
		}
	}
`;

function TableHeader(props: PropsWithChildren) {
	return <TableHeaderContainer>{props.children}</TableHeaderContainer>;
}

Table.Header = TableHeader;

const TableFooterContainer = styled(Flex)`
	min-width: min-content;
	padding: ${tokens.padding.control}px ${tokens.padding.container}px;
	border-top: 1px solid ${tokens.border.subtle};

	${TableCellContainer} {
		font-weight: ${tokens.typography.weights.medium};
	}
`;

function TableFooter(props: PropsWithChildren) {
	return <TableFooterContainer>{props.children}</TableFooterContainer>;
}

Table.Footer = TableFooter;

const PaginationRow = styled(Flex)`
	align-items: center;
	justify-content: space-between;
	padding: ${tokens.padding.control}px ${tokens.padding.container}px;
	box-sizing: border-box;
`;

function TablePagination(props: {
	disabled: {
		previous: boolean;
		next: boolean;
	};
	pagination: {
		currentPage: number;
		perPage: number;
		max: number;
	};
	onPrevious: () => void;
	onNext: () => void;
}) {
	const { t } = useTranslation();
	const { pagination } = props;

	return (
		<PaginationRow>
			<Box>
				<Paragraph>
					{t('table.pagination.ofThe', {
						progress: `${(pagination.currentPage - 1) * pagination.perPage + 1} - ${Math.min(
							pagination.max,
							pagination.currentPage * pagination.perPage,
						)}`,
						max: pagination.max,
					})}
				</Paragraph>
			</Box>

			<Flex>
				<Button
					small
					type="card"
					title={t('table.pagination.previous')}
					disabled={props.disabled.previous}
					onClick={() => props.onPrevious()}
					icon={<ChevronLeftIcon />}
				/>
				<Button
					ml={2}
					small
					type="card"
					title={t('table.pagination.next')}
					disabled={props.disabled.next}
					onClick={() => props.onNext()}
					rightIcon={<ChevronRightIcon />}
				/>
			</Flex>
		</PaginationRow>
	);
}

Table.Pagination = TablePagination;

function TablePaging(props: {
	pageSize?: number;
	pageInfo?: graphql.PageInfo;
	onPaginate: (pagination: Pagination) => void;
}) {
	const { t } = useTranslation();

	return (
		<Flex mt={3} justifyContent="flex-end">
			<Box mr={2}>
				<Button
					title={t('common.back')}
					small
					type="card"
					disabled={!props.pageInfo?.hasPreviousPage}
					onClick={() => {
						props.onPaginate({ last: props.pageSize ?? 10, before: props.pageInfo?.startCursor });
					}}
				/>
			</Box>
			<Button
				title={t('common.next')}
				small
				type="card"
				disabled={!props.pageInfo?.hasNextPage}
				onClick={() => {
					props.onPaginate({ first: props.pageSize ?? 10, after: props.pageInfo?.endCursor });
				}}
			/>
		</Flex>
	);
}

Table.Paging = TablePaging;

export type Pagination =
	| {
			first: number;
			after?: string | null;
	  }
	| {
			last: number;
			before?: string | null;
	  };

function TableList<T>({
	data,
	error,
	loading,
	transformItem,
	headers,
	noDataText,
}: {
	data?: T[];
	error?: Error;
	loading?: boolean;
	transformItem: (item: T) => { link: string; cells: string[] };
	headers: string[];
	noDataText?: string;
}) {
	const { t } = useTranslation();

	if (loading) {
		return <Spinner centered />;
	}

	if (error) {
		return <GraphQLErrorMessage error={error} />;
	}

	const hasData = data && data.length > 0;

	return (
		<Table>
			{hasData && (
				<Table.Header>
					{headers.map((header) => (
						<Table.Cell basis={1} key={header}>
							{header}
						</Table.Cell>
					))}
				</Table.Header>
			)}
			{hasData ? (
				data.map(transformItem).map((item, index) => {
					return (
						<Table.Row key={index} tall link={item.link}>
							{item.cells.map((cell) => (
								<Table.Cell basis={1} key={cell}>
									{cell}
								</Table.Cell>
							))}
						</Table.Row>
					);
				})
			) : (
				<Table.Row key={'no-data-row'} tall>
					<Table.Cell basis={1} key={'no-data-cell'} align={'center'}>
						{noDataText || t('common.listEmpty')}
					</Table.Cell>
				</Table.Row>
			)}
		</Table>
	);
}

Table.List = TableList;
